import React, { useState, useEffect } from 'react';
import { useOrgChart } from '../contexts/OrgChartContext';
import './OrgChartDisplay.css';

function OrgChartDisplay() {
  const { orgNodes, loading, error } = useOrgChart();
  const [expandedNodes, setExpandedNodes] = useState({});
  const [zoomed, setZoomed] = useState(false);
  const [nodeHighlight, setNodeHighlight] = useState(null);

  // Initialize all nodes to expanded state
  useEffect(() => {
    if (orgNodes && orgNodes.length > 0) {
      const initialExpandedState = {};
      orgNodes.forEach(node => {
        initialExpandedState[node.id] = true;
      });
      setExpandedNodes(initialExpandedState);
    }
  }, [orgNodes]);

  // Toggle node expansion
  const toggleNodeExpansion = (e, nodeId) => {
    e.stopPropagation(); // Prevent event bubbling
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  // Highlight node
  const handleNodeClick = (nodeId) => {
    setNodeHighlight(nodeHighlight === nodeId ? null : nodeId);
  };

  // Toggle zoom view
  const toggleZoom = () => {
    setZoomed(!zoomed);
  };

  // Build hierarchical data structure for display
  const buildOrgChartTree = () => {
    // Map for faster lookups
    const nodeMap = {};
    orgNodes.forEach(node => {
      nodeMap[node.id] = { ...node, children: [] };
    });
    
    // Build tree structure
    const rootNodes = [];
    orgNodes.forEach(node => {
      if (node.parentId) {
        if (nodeMap[node.parentId]) {
          nodeMap[node.parentId].children.push(nodeMap[node.id]);
        }
      } else {
        rootNodes.push(nodeMap[node.id]);
      }
    });
    
    // Sort children by their order
    const sortChildren = (node) => {
      if (node.children && node.children.length > 0) {
        node.children.sort((a, b) => a.order - b.order);
        node.children.forEach(sortChildren);
      }
      return node;
    };
    
    rootNodes.sort((a, b) => a.order - b.order);
    rootNodes.forEach(sortChildren);
    
    return rootNodes;
  };

  // Calculate node counts for balanced layout
  const getNodeLevels = (node, level = 0, levels = {}) => {
    if (!levels[level]) {
      levels[level] = 0;
    }
    levels[level]++;

    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        getNodeLevels(child, level + 1, levels);
      });
    }
    return levels;
  };

  // Render tree recursively with enhanced styling
  const renderNode = (node, level = 0, isLast = false, isFirst = false, parentWidth = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes[node.id] !== false; // Default to expanded
    const isHighlighted = nodeHighlight === node.id;
    
    const nodeClasses = [
      "org-node",
      `level-${level}`,
      isLast ? "is-last" : "",
      isFirst ? "is-first" : "",
      hasChildren ? "has-children" : "",
      isExpanded && hasChildren ? "is-expanded" : "",
      isHighlighted ? "is-highlighted" : ""
    ].filter(Boolean).join(" ");

    // Position indicator based on level
    const positionClass = getPositionClass(level);

    return (
      <div key={node.id} className={nodeClasses}>
        <div 
          className={`org-node-content ${positionClass}`}
          onClick={() => handleNodeClick(node.id)}
        >
          {node.imageBase64 ? (
            <div className="org-node-image">
              <img src={node.imageBase64} alt={node.name} />
            </div>
          ) : (
            <div className="org-node-image org-node-placeholder">
              <span>{node.name.charAt(0)}</span>
            </div>
          )}
          <div className="org-node-details">
            <h4>{node.name}</h4>
            {node.title && <p className="org-node-title">{node.title}</p>}
          </div>
          {hasChildren && (
            <button 
              className="toggle-children" 
              onClick={(e) => toggleNodeExpansion(e, node.id)}
              title={isExpanded ? "Collapse" : "Expand"}
            >
              <span className="toggle-icon">{isExpanded ? "−" : "+"}</span>
            </button>
          )}
        </div>
        
        {hasChildren && isExpanded && (
          <div className="org-node-children-container">
            <div className="org-node-connector"></div>
            <div className="org-node-children">
              {node.children.map((child, index) => 
                renderNode(
                  child, 
                  level + 1, 
                  index === node.children.length - 1,
                  index === 0,
                  node.children.length
                )
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Get position class based on level in hierarchy
  const getPositionClass = (level) => {
    switch (level) {
      case 0: return "position-top";
      case 1: return "position-senior";
      case 2: return "position-middle";
      case 3: return "position-junior";
      default: return "position-staff";
    }
  };

  if (loading) {
    return (
      <div className="org-chart-display-container">
        <div className="chart-header">
          <h2 className="section-title">ORGANIZATIONAL CHART</h2>
        </div>
        <div className="loading-message">
          <div className="loading-spinner"></div>
          <p>Loading organizational chart...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="org-chart-display-container">
        <div className="chart-header">
          <h2 className="section-title">ORGANIZATIONAL CHART</h2>
        </div>
        <div className="error-message">
          <span className="error-icon">⚠</span>
          <p>Error loading organizational chart: {error}</p>
        </div>
      </div>
    );
  }

  const orgChartData = buildOrgChartTree();

  return (
    <div className={`org-chart-display-container ${zoomed ? 'zoomed-view' : ''}`}>
      <div className="chart-header">
        <h2 className="section-title">ORGANIZATIONAL CHART</h2>
        <div className="chart-controls">
          <button className="chart-control-btn" onClick={toggleZoom} title={zoomed ? "Normal View" : "Full Screen View"}>
            <i className={`bi ${zoomed ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`}></i>
          </button>
          <button className="chart-control-btn" onClick={() => setNodeHighlight(null)} title="Clear Selection">
            <i className="bi bi-eraser"></i>
          </button>
          <button className="chart-control-btn" onClick={() => {
            const allExpanded = Object.values(expandedNodes).every(Boolean);
            const newState = !allExpanded;
            const updatedExpandedNodes = {};
            orgNodes.forEach(node => {
              updatedExpandedNodes[node.id] = newState;
            });
            setExpandedNodes(updatedExpandedNodes);
          }} title="Expand/Collapse All">
            <i className="bi bi-arrows-angle-expand"></i>
          </button>
        </div>
      </div>
      
      {orgChartData.length === 0 ? (
        <div className="no-data-message">
          <span className="no-data-icon">📋</span>
          <p>No organizational chart data available</p>
        </div>
      ) : (
        <div className="org-chart-wrapper">
          <div className="org-chart-public-view">
            {orgChartData.map((node, index) => 
              renderNode(node, 0, index === orgChartData.length - 1, index === 0)
            )}
          </div>
        </div>
      )}
      
      <div className="org-chart-legend">
        <div className="legend-title">Position Levels</div>
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-color position-top"></div>
            <div className="legend-label">Executive</div>
          </div>
          <div className="legend-item">
            <div className="legend-color position-senior"></div>
            <div className="legend-label">Senior Management</div>
          </div>
          <div className="legend-item">
            <div className="legend-color position-middle"></div>
            <div className="legend-label">Middle Management</div>
          </div>
          <div className="legend-item">
            <div className="legend-color position-junior"></div>
            <div className="legend-label">Junior Management</div>
          </div>
          <div className="legend-item">
            <div className="legend-color position-staff"></div>
            <div className="legend-label">Staff</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrgChartDisplay; 